import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, LogOut, TrendingDown } from 'lucide-react';
import { MOTIVOS_SALIDA_BODEGA } from '@suminuta/shared';
import type { MotivoSalidaBodega } from '@suminuta/shared';
import type { Ingrediente } from '../types/ingredientes';
import type { SalidaBodegaPayload } from '../services/ingredientesService';
import { obtenerFechaActualLocal } from '../utils/dateUtils';

/**
 * Registro manual de una salida de bodega (PROMPT_N°046).
 *
 * Durante la marcha blanca es la única forma de anotar lo que sale: las
 * entradas vienen por OC/factura y Cocina —que descuenta sola— todavía no
 * se usa. La cantidad se ingresa en la unidad display del ingrediente, la
 * misma que Juan reporta en la planilla.
 *
 * Sigue el molde de `ModalAjusteStock`: Tailwind + lucide, sin HeroUI, que
 * es lo que usa el resto de la pantalla de Ingredientes.
 */

interface ModalSalidaBodegaProps {
  isOpen: boolean;
  onClose: () => void;
  ingrediente: Ingrediente | null;
  onConfirm: (data: SalidaBodegaPayload) => void;
  isLoading?: boolean;
}

const MOTIVOS = Object.entries(MOTIVOS_SALIDA_BODEGA) as Array<
  [MotivoSalidaBodega, string]
>;

const ModalSalidaBodega: React.FC<ModalSalidaBodegaProps> = ({
  isOpen,
  onClose,
  ingrediente,
  onConfirm,
  isLoading = false,
}) => {
  const [cantidad, setCantidad] = useState<string>('');
  const [motivo, setMotivo] = useState<MotivoSalidaBodega>('consumo_cocina');
  const [destino, setDestino] = useState<string>('');
  const [fecha, setFecha] = useState<string>(obtenerFechaActualLocal());
  const [notas, setNotas] = useState<string>('');

  useEffect(() => {
    if (isOpen && ingrediente) {
      setCantidad('');
      setMotivo('consumo_cocina');
      setDestino('');
      setFecha(obtenerFechaActualLocal());
      setNotas('');
    }
  }, [isOpen, ingrediente]);

  const stockActual = ingrediente?.stockActual ?? 0;
  const unidad = ingrediente?.unidadMedida?.abreviacion ?? '';
  const cantidadNum = parseFloat(cantidad);

  // Validación en vivo: el botón Guardar sólo se habilita con una cantidad
  // que el stock aguanta. El backend revalida igual y responde 409.
  const errorCantidad = useMemo(() => {
    if (cantidad.trim() === '') return null;
    if (isNaN(cantidadNum)) return 'Ingresá un número válido';
    if (cantidadNum <= 0) return 'La cantidad debe ser mayor a 0';
    if (cantidadNum > stockActual) {
      return `Supera el stock disponible (${stockActual} ${unidad})`;
    }
    return null;
  }, [cantidad, cantidadNum, stockActual, unidad]);

  const puedeGuardar =
    !isLoading &&
    cantidad.trim() !== '' &&
    !isNaN(cantidadNum) &&
    cantidadNum > 0 &&
    cantidadNum <= stockActual &&
    notas.length <= 500 &&
    destino.length <= 100;

  if (!isOpen || !ingrediente) return null;

  const stockResultante = Math.max(0, stockActual - (cantidadNum || 0));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar) return;

    onConfirm({
      cantidad: cantidadNum,
      motivo,
      fecha,
      destino: destino.trim() || undefined,
      notas: notas.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 p-2">
              <LogOut className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Registrar salida de bodega
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {ingrediente.nombre} —{' '}
                <span className="font-medium">
                  Stock actual: {stockActual} {unidad}
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            disabled={isLoading}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Cantidad */}
          <div>
            <label
              htmlFor="salida-cantidad"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Cantidad a retirar *
            </label>
            <div className="relative">
              <input
                type="number"
                id="salida-cantidad"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className={`w-full px-3 py-2 pr-16 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                  errorCantidad
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={`Cantidad en ${unidad}`}
                step="0.01"
                min="0"
                max={stockActual}
                disabled={isLoading}
                autoFocus
              />
              <div className="absolute right-3 top-2 text-gray-500 dark:text-gray-400">
                {unidad}
              </div>
            </div>
            {errorCantidad && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                {errorCantidad}
              </p>
            )}
            {!errorCantidad && cantidadNum > 0 && (
              <div className="mt-3 p-3 rounded-lg flex items-center gap-2 bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                <TrendingDown size={20} />
                <div className="text-sm">
                  Quedan{' '}
                  <span className="font-semibold">
                    {stockResultante.toFixed(2)} {unidad}
                  </span>{' '}
                  después de esta salida.
                </div>
              </div>
            )}
          </div>

          {/* Motivo + Destino */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="salida-motivo"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Motivo *
              </label>
              <select
                id="salida-motivo"
                value={motivo}
                onChange={(e) =>
                  setMotivo(e.target.value as MotivoSalidaBodega)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={isLoading}
              >
                {MOTIVOS.map(([valor, etiqueta]) => (
                  <option key={valor} value={valor}>
                    {etiqueta}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="salida-destino"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Destino
              </label>
              <input
                type="text"
                id="salida-destino"
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Cocina, casino turno noche…"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Fecha */}
          <div>
            <label
              htmlFor="salida-fecha"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Fecha de la salida
            </label>
            <input
              type="date"
              id="salida-fecha"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Por defecto hoy. Cambiala si estás cargando una planilla de un
              día anterior.
            </p>
          </div>

          {/* Notas */}
          <div>
            <label
              htmlFor="salida-notas"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Notas (opcional)
            </label>
            <textarea
              id="salida-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Detalle de la salida, quién la retiró, etc."
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {notas.length}/500 caracteres
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={!puedeGuardar}
            >
              {isLoading ? 'Registrando…' : 'Registrar salida'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalSalidaBodega;
